import React from 'react';
import './expandableRow.css';

/**
 * Shared accordion row with the seared "selected" treatment — the gold
 * border + glow + warm body gradient used on the Cards pane. Consumers pass
 * their own header content and body; the row handles the toggle button,
 * affordance, transitions, and the expanded visual state.
 *
 * Usage:
 *   <ExpandableRow
 *       expanded={isOpen}
 *       onToggle={() => setOpen(!isOpen)}
 *       tone="red"
 *       header={<MyHeaderContent />}
 *   >
 *       <MyBodyContent />
 *   </ExpandableRow>
 */
export function ExpandableRow({
    header,
    children,
    expanded,
    onToggle,
    tone = 'red',
    rowClassName = '',
    bodyClassName = '',
    headerClassName = '',
    ariaLabel,
}) {
    const cls = [
        'expandableRow',
        `expandableRow--${tone}`,
        expanded ? 'is-expanded' : '',
        rowClassName,
    ]
        .filter(Boolean)
        .join(' ');
    return (
        <div className={cls}>
            <button
                type="button"
                className={`expandableRow__header ${headerClassName}`.trim()}
                onClick={onToggle}
                aria-expanded={!!expanded}
                aria-label={ariaLabel}
            >
                <div className="expandableRow__headerContent">{header}</div>
                <span
                    className="expandableRow__affordance"
                    aria-hidden="true"
                    data-expanded={expanded ? 'true' : 'false'}
                >
                    <span className="expandableRow__affordanceLabel">
                        {expanded ? 'Hide' : 'Details'}
                    </span>
                    <svg
                        className="expandableRow__chevron"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <polyline points="4,6 8,10 12,6" />
                    </svg>
                </span>
            </button>
            {expanded && (
                <div className={`expandableRow__body ${bodyClassName}`.trim()}>{children}</div>
            )}
        </div>
    );
}

export default ExpandableRow;
